/**
 * 67.75, the utility drone (DECISIONS 224 (5), the owner: "we probably also will want to introduce some smaller utility drone
 * that can deliver supplies into the field, but is only big enough to carry one of those smaller supply loads"; on 1h: "don't
 * love the quadcopter drone, not sure what I want but probably don't want just a quadcopter, but probably something with
 * quadcopter flight characteristics."; on 1i's three: "I think nacelle lifter with more cagelike container is good for
 * drone."). The nacelle lifter (1i's A: the dropship's quad in miniature) carrying its drop crate (constants/logistics.ts
 * DROP_CRATE: a point crate's refill) in a cage like 1i's B (the frame family's posts, rails, corner castings and braces):
 *   - **the cage** (1.0 × 1.3 m, 0.82 m tall): the crate held in it by four clamps from its top rails; landed, the crate stands
 *     on the ground inside it, and the drone lifts off and leaves it;
 *   - **the fairing** over the cage: a short lifting body, its nose ahead, the sensors on it;
 *   - **four nacelles** on stubs off the fairing, upright for the hover (intakes up, nozzles down): the dropship's own profile
 *     (rendering/dropshipLooks.ts NACELLE) at 0.42.
 * It flies as a multirotor (hover, vertical take-off and landing, slow precise moves). In the fleet's language: the company's
 * paint, a cream placard with its name on each long side, AUTO, caution bands, a lidar puck, a beacon. Two tiers (full; far:
 * the cage's frame, the fairing, the nacelles as boxes), Lambert per vertex: one draw each. Its origin the ground under it,
 * landed; a flight lifts it. Design sheet only (design_sheets/supplies.html).
 */
import * as THREE from 'three';
import { Spec, mergedTier, type Livery } from './orbitShips';
import { dropCrateSpec } from './supplyLoads';
import { DROP_CRATE } from '../constants/logistics';
import type { UnitPalette } from './unitPalette';

/** The cage (metres): across (x), along (z), tall; the crate stands inside it. */
export const DRONE_CAGE = { width: 1.0, length: 1.3, height: 0.82 };

const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
/** The dropship's nacelle profile (z, y about its shaft; dropshipLooks.ts NACELLE). */
const NACELLE = [1.3, -0.33, 1.15, 0.42, 0.45, 0.5, -1.0, 0.45, -1.35, 0.26, -1.35, -0.26, -1.0, -0.42, 0.45, -0.46, 1.15, -0.42];

/** A rotor's blades (two, crossed in the full tier) and its hub at (x, y, z), `r` its radius. */
function rotor(s: Spec, x: number, y: number, z: number, r: number, turn = 0): void {
  s.box(2 * r, 0.015, 0.07, x, y, z, 'machDk', { ry: turn });
  s.box(2 * r, 0.015, 0.07, x, y, z, 'machDk', { ry: turn + Math.PI / 2, tier: 'full' });
  s.cyl(0.05, 0.05, 'y', x, y + 0.02, z, 'metal', { seg: 8, tier: 'full' });
}

/** The cage: posts, top and bottom rails, corner castings, the long sides braced, a placard with the name on each. */
function cage(s: Spec): void {
  const { width: W, length: L, height: H } = DRONE_CAGE, e = 0.06;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    s.box(e, H, e, sx * (W / 2 - e / 2), H / 2, sz * (L / 2 - e / 2), 'team');
    for (const y of [0.045, H - 0.045]) s.box(0.09, 0.09, 0.09, sx * (W / 2 - 0.04), y, sz * (L / 2 - 0.04), 'machLt', { tier: 'full' });
  }
  for (const y of [0.04, H - 0.03]) {
    for (const sx of [-1, 1]) s.box(e, 0.06, L, sx * (W / 2 - e / 2), y, 0, 'team');
    for (const sz of [-1, 1]) s.box(W, 0.06, e, 0, y, sz * (L / 2 - e / 2), 'team');
  }
  const diag = Math.hypot(L - 0.1, H - 0.1), a = Math.atan2(H - 0.1, L - 0.1);
  for (const sx of [-1, 1]) for (const r of [a, -a]) s.box(0.035, 0.035, diag, sx * (W / 2 - e / 2), H / 2, 0, 'teamDk', { rx: r, tier: 'full' });
  for (const sx of [-1, 1]) s.box(0.02, 0.24, 0.7, sx * (W / 2 + 0.005), H * 0.5, 0, 'cream', { tier: 'full' });
  s.name(W / 2 + 0.017, H * 0.54, 0, 0.09, 0.62, 'team');
  s.text('AUTO', W / 2 + 0.017, H * 0.42, 0, 0.055, 0.3, 'caution');
  // four clamps from the top rails to the crate's lifting eyes
  const ct = 0.06 + DROP_CRATE.height + 0.02;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) s.rod(V(sx * (W / 2 - 0.08), H - 0.06, sz * (L / 2 - 0.25)), V(sx * (DROP_CRATE.length / 2 - 0.08), ct, sz * (DROP_CRATE.width / 2 - 0.08)), 0.02, 'metal', { seg: 4, tier: 'full' });
}

/** The fairing over the cage (its nose ahead, +z), the sensors, the stubs and the four upright nacelles. */
function lifter(s: Spec): void {
  const H = DRONE_CAGE.height;
  s.side([0.92, H + 0.02, 0.8, H + 0.2, 0.42, H + 0.3, -0.62, H + 0.28, -0.82, H + 0.16, -0.82, H + 0.02], 1.02, 0, 'team', { chamfer: 0.04 });
  s.box(1.04, 0.05, 1.3, 0, H + 0.17, -0.05, 'teamLt');
  s.box(0.5, 0.04, 0.24, 0, H + 0.3, 0.42, 'cream', { tier: 'full' });   // (the sensor window ahead)
  s.cyl(0.08, 0.07, 'y', 0, H + 0.33, 0.05, 'machDk', { seg: 10, tier: 'full' });
  s.cyl(0.065, 0.045, 'y', 0, H + 0.39, 0.05, 'cream', { seg: 10, tier: 'full' });   // (the lidar puck)
  s.sphere(0.055, 0, H + 0.34, -0.55, 'caution');   // (the beacon)
  s.sphere(0.06, 0, H + 0.04, 0.9, 'machDk', { tier: 'full' });   // (the camera ball under the nose)
  for (const sz of [-1, 1]) s.box(0.5, 0.06, 0.04, 0, H + 0.08, sz * 0.9, 'caution', { tier: 'full' });
  const k = 0.42, N = NACELLE.map(v => v * k), up = new THREE.Matrix4().makeRotationX(-Math.PI / 2), top = 1.3 * k, foot = 1.35 * k, y = H + 0.16;
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const x = sx * 0.96, z = sz * 0.68;
    s.box(0.42, 0.1, 0.22, sx * 0.68, y, z, 'teamDk');   // (the stub)
    s.within(new THREE.Matrix4().makeTranslation(x, y, z).multiply(up), () => {
      s.side(N, 0.36, 0, 'team', { chamfer: 0.025, tier: 'full' });
      s.box(0.38, 0.1, 0.4, 0, 0.05, -0.18, 'teamLt', { tier: 'full' });   // (the team's band)
    });
    s.box(0.36, top + foot, 0.4, x, y + (top - foot) / 2, z, 'team', { tier: 'far' });
    s.cyl(0.15, 0.03, 'y', x, y + top + 0.005, z, 'rubber', { seg: 12, tier: 'full' });   // (the intake's mouth, its fan inside)
    rotor(s, x, y + top - 0.02, z, 0.13, sx * sz * 0.6);
    s.cyl(0.12, 0.14, 'y', x, y - foot - 0.05, z, 'metal', { seg: 10, tier: 'full' });   // (the nozzle)
  }
}

export interface UtilityDroneLook { full: THREE.Mesh; far: THREE.Mesh; triangles: { full: number; far: number } }

/** The utility drone, with its drop crate in its cage or not: both tiers, one draw each, and their triangles. */
export function buildUtilityDrone(p: UnitPalette, livery: Livery, o: { crate?: boolean } = {}): UtilityDroneLook {
  const s = new Spec(livery);
  cage(s);
  lifter(s);
  if (o.crate ?? true) dropCrateSpec(s, 0.06);
  const mesh = (far: boolean) => {
    const g = mergedTier(s, p, far);
    g.setAttribute('color', g.getAttribute('paint'));
    g.deleteAttribute('glow');
    return new THREE.Mesh(g, new THREE.MeshLambertMaterial({ vertexColors: true }));
  };
  const full = mesh(false), far = mesh(true);
  return { full, far, triangles: { full: full.geometry.attributes.position.count / 3, far: far.geometry.attributes.position.count / 3 } };
}
